import { useState } from "react";
import { useFormik } from "formik";
import { isEmpty } from "lodash";
import { Avatar, Button, Grid2, TextField, Typography } from "@mui/material";
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import TableCell from "../../components/CertificateTable/TableCell";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUser } from "../../store/userSlice/userSelectors";
import { formatDate } from "../../helpers/date";
import { logout } from "../../store/userSlice/userSlice";
import { userProfileValidation as validationSchema } from "../../validations/userProfile";
import { palette } from "../../theme";
import { updateProfile } from "../../store/userSlice/userRequests";

import classes from './index.module.scss';

const ProfileContainer = () => {
  const dispatch = useDispatch();
  const [isEdit, setEdit] = useState(false)
  const user = useSelector(selectCurrentUser);
  const name = user?.firstName;
  const userId = user?.id;
  const fullName = (user?.firstName || '') + ' ' + (user?.lastName || '');
  const avatarName = (user?.firstName || '').slice(0, 1) + (user?.lastName || '').slice(0, 1) || user?.email.slice(0, 1);
  const registeredDate = formatDate(user?.createdAt, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }, 'en-US');

  const initialValues = {
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    companyName: user?.companyName || '',
    email: user?.email || '',
    phoneNumber: user?.phoneNumber || '',
    username: user?.username || '',
    password: '',
    confirmPassword: '',
  }

  const profileFormik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => {},
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
  });

  const onStartEdit = async () => {
    if (isEdit) {
      const errorList = await profileFormik.validateForm();

      if (isEmpty(errorList)) {
        dispatch(
          updateProfile({
            id: userId,
            ...profileFormik.values,
          })
        ).then(() => {
          window.location.reload();
        })
      }
      return;
    }

    setEdit(!isEdit);
  }

  const onCancel = () => {
    profileFormik.resetForm(initialValues);
    setEdit(false);
  }

  const onLogout = () => {
    dispatch(logout());
    window.location.reload();
  }

  return (
    <Grid2 container size={12} className={classes.profileContainer}>
      <Grid2 container size={12}>
        <TableCell size={12} clearBorderBottom fullWidth>
          <Grid2 container direction="row" className={classes.rowContainer}>
            <Grid2 className={classes.rest}>
              <Grid2 container direction="column" size={12}>
                <Grid2 size={12}>
                  <Typography variant="h2" className={classes.welcome}>
                    Welcome
                    {
                      name ? `, ${name}` : ''
                    }
                  </Typography>
                </Grid2>
                <Grid2 size={12}>
                  <Typography variant="body2">
                    Registered: {registeredDate}
                  </Typography>
                </Grid2>
              </Grid2>
            </Grid2>
            <Grid2 className={classes.right}>
              <Grid2 container spacing={2}>
                <Grid2>
                  <Button variant="outlined" color="secondary" startIcon={<EditNoteOutlinedIcon />}
                          onClick={onStartEdit}>
                    {
                      isEdit ? "Save" : "Edit"
                    }
                  </Button>
                </Grid2>
                {
                  isEdit ? (
                    <Grid2>
                      <Button variant="outlined" color="secondary" startIcon={<CancelOutlinedIcon />}
                              onClick={onCancel}>Cancel</Button>
                    </Grid2>
                  ) : null
                }
                <Grid2>
                  <Button variant="outlined" color="secondary" startIcon={<LogoutOutlinedIcon />} onClick={onLogout}>
                    Log out
                  </Button>
                </Grid2>
              </Grid2>
            </Grid2>
          </Grid2>
        </TableCell>
        <TableCell fullWidth size={12}>
          <Grid2 container size={12} className={classes.avatarContainer}>
            <Grid2>
              <Avatar
                sx={{ bgcolor: palette.primary.main, width: 100, height: 100, fontSize: 32, fontWeight: 600 }}
              >
                {avatarName}
              </Avatar>
            </Grid2>
            <Grid2 className={classes.userName}>
              <Typography variant='h2'>{fullName}</Typography>
            </Grid2>
          </Grid2>
          <Grid2 container size={12} spacing={2} className={classes.formContainer}>
            <Grid2 size={4}>
              <Typography variant='body1' className={classes.inputText}>First Name</Typography>
              <TextField
                variant='outlined'
                name='firstName'
                size='medium'
                fullWidth
                disabled={!isEdit}
                value={profileFormik.values?.firstName}
                onChange={profileFormik.handleChange}
              />
            </Grid2>
            <Grid2 size={4}>
              <Typography variant='body1' className={classes.inputText}>Username</Typography>
              <TextField
                variant='outlined'
                name='username'
                size='medium'
                fullWidth
                disabled
                value={profileFormik.values?.username}
                onChange={profileFormik.handleChange}
              />
            </Grid2>
            <Grid2 size={4}>
              <Typography variant='body1' className={classes.inputText}>Phone</Typography>
              <TextField
                variant='outlined'
                name='phoneNumber'
                size='medium'
                fullWidth
                disabled={!isEdit}
                value={profileFormik.values?.phoneNumber}
                onChange={profileFormik.handleChange}
              />
            </Grid2>

            <Grid2 size={4}>
              <Typography variant='body1' className={classes.inputText}>Last Name</Typography>
              <TextField
                variant='outlined'
                name='lastName'
                size='medium'
                fullWidth
                disabled={!isEdit}
                value={profileFormik.values?.lastName}
                onChange={profileFormik.handleChange}
              />
            </Grid2>
            <Grid2 size={4}>
              <Typography variant='body1' className={classes.inputText}>Company Name</Typography>
              <TextField
                variant='outlined'
                name='companyName'
                size='medium'
                fullWidth
                disabled={!isEdit}
                value={profileFormik.values?.companyName}
                onChange={profileFormik.handleChange}
              />
            </Grid2>
            <Grid2 size={4}>
              <Typography variant='body1' className={classes.inputText}>Email</Typography>
              <TextField
                variant='outlined'
                name='email'
                size='medium'
                fullWidth
                disabled={!isEdit}
                value={profileFormik.values?.email}
                onChange={profileFormik.handleChange}
                error={Boolean(profileFormik?.errors?.email)}
                helperText={profileFormik?.errors?.email}
              />
            </Grid2>
            {
              isEdit ? (
                <Grid2 size={4}>
                  <Typography variant='body1' className={classes.inputText}>Password</Typography>
                  <TextField
                    variant='outlined'
                    name='password'
                    size='medium'
                    type='password'
                    fullWidth
                    disabled={!isEdit}
                    value={profileFormik.values?.password}
                    onChange={profileFormik.handleChange}
                    error={Boolean(profileFormik?.errors?.password)}
                    helperText={profileFormik?.errors?.password}
                  />
                </Grid2>
              ) : null
            }
            {
              isEdit ? (
                <Grid2 size={4}>
                  <Typography variant='body1' className={classes.inputText}>Confirm Password</Typography>
                  <TextField
                    variant='outlined'
                    name='confirmPassword'
                    size='medium'
                    type='password'
                    fullWidth
                    disabled={!isEdit}
                    value={profileFormik.values?.confirmPassword}
                    onChange={profileFormik.handleChange}
                    error={Boolean(profileFormik?.errors?.confirmPassword)}
                    helperText={profileFormik?.errors?.confirmPassword}
                  />
                </Grid2>
              ) : null
            }
          </Grid2>
        </TableCell>
      </Grid2>
    </Grid2>
  );
}

export default ProfileContainer;